import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Fire, Trash } from "@phosphor-icons/react";

const dayNames = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

export default function Habits() {
  const [habits, setHabits] = useState([]);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => api.get("/habits").then((r) => setHabits(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/habits", { name });
      setName("");
      setOpen(false);
      toast.success("Привычка добавлена");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (id, dateStr) => {
    try { await api.post(`/habits/${id}/toggle`, { date: dateStr }); load(); }
    catch { toast.error("Ошибка"); }
  };

  const remove = async (id) => {
    await api.delete(`/habits/${id}`);
    toast.success("Привычка удалена");
    load();
  };

  return (
    <div className="fade-in" data-testid="habits-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Трекер</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Привычки</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-habit-button" className="active:scale-95 transition-transform">
              <Plus size={16} className="mr-1" /> Новая привычка
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-modal">
            <DialogHeader><DialogTitle>Новая привычка</DialogTitle></DialogHeader>
            <form onSubmit={add} className="space-y-4">
              <Input
                data-testid="habit-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: Медитация 10 минут"
                required
              />
              <Button type="submit" className="w-full" data-testid="habit-submit-button">Добавить</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {habits.length === 0 ? (
        <div className="glass-card p-12 text-center text-muted-foreground" data-testid="habits-empty">
          Пока нет привычек. Добавьте первую!
        </div>
      ) : (
        <div className="glass-card overflow-x-auto">
          <div className="min-w-[560px]">
          <div className="grid grid-cols-[1fr_repeat(7,40px)_52px_36px] sm:grid-cols-[1fr_repeat(7,44px)_60px_40px] gap-0 px-3 sm:px-6 py-3 border-b border-border/40 text-xs uppercase tracking-widest text-muted-foreground">
            <span>Привычка</span>
            {habits[0].week.map((d, i) => {
              const wd = (new Date(d.date).getDay() + 6) % 7;
              return <span key={i} className="text-center">{dayNames[wd]}</span>;
            })}
            <span className="text-center">Серия</span>
            <span />
          </div>
          {habits.map((h) => (
            <div key={h.id} className="grid grid-cols-[1fr_repeat(7,40px)_52px_36px] sm:grid-cols-[1fr_repeat(7,44px)_60px_40px] items-center px-3 sm:px-6 py-3 border-b border-border/40 last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`habit-row-${h.id}`}>
              <span className="text-sm font-medium truncate pr-4">{h.name}</span>
              {h.week.map((d) => (
                <button
                  key={d.date}
                  onClick={() => toggle(h.id, d.date)}
                  data-testid={`habit-cell-${h.id}-${d.date}`}
                  title={d.date}
                  className={`w-7 h-7 mx-auto rounded transition-colors ${
                    d.done ? "bg-[#00e676] hover:bg-[#00e676]/80" : "bg-secondary hover:bg-secondary/60 border border-border"
                  }`}
                />
              ))}
              <span className="flex items-center justify-center gap-1 text-sm num text-[#ffea00]">
                {h.streak > 0 && <Fire size={14} weight="fill" />}{h.streak}
              </span>
              <button onClick={() => remove(h.id)} data-testid={`habit-delete-${h.id}`} className="text-muted-foreground hover:text-destructive transition-colors mx-auto">
                <Trash size={16} />
              </button>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
